import * as React from 'react';

/**
 * A hook that debounces a loading state to prevent flickering for fast operations.
 * Only shows loading state if it persists longer than the specified delay.
 * 
 * @param isLoading - The current loading state
 * @param delay - The delay in milliseconds before showing loading state
 * @returns The debounced loading state
 */
function useDebouncedLoading(isLoading: boolean, delay: number = 250): boolean {
  const [debouncedLoading, setDebouncedLoading] = React.useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    // If loading starts, set a timeout before showing loading state
    if (isLoading && !debouncedLoading) {
      timeoutRef.current = setTimeout(() => {
        setDebouncedLoading(true);
      }, delay);
    }
    // If loading ends, clear any pending timeout and hide loading immediately
    else if (!isLoading && debouncedLoading) {
      setDebouncedLoading(false);
    }

    // Cleanup timeout on unmount or when loading state changes
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isLoading, debouncedLoading, delay]);

  return debouncedLoading;
}

export default useDebouncedLoading;
